import React from 'react';
import './BlueskyPostCard.css';

const BlueskyPostCard = ({ post, level = 'root', compact = false }) => {
  if (!post) return null;

  const { author, text, embed, createdAt, likeCount, replyCount, repostCount } = post;

  // Format timestamp
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins}m`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
    return `${Math.floor(diffMins / 1440)}d`;
  };

  // Truncate text for compact mode
  const displayText = compact && text && text.length > 200 
    ? text.substring(0, 200) + '...' 
    : text;

  // Render embed (images, external links, etc.)
  const renderEmbed = () => {
    if (!embed) return null;

    // Image embed
    if (embed.$type === 'app.bsky.embed.images#view' && embed.images) {
      return (
        <div className="post-images">
          {embed.images.slice(0, 4).map((img, i) => (
            <img 
              key={i}
              src={img.thumb || img.fullsize} 
              alt={img.alt || 'Image'}
              className={`post-image ${embed.images.length === 1 ? 'single' : ''}`}
            />
          ))}
        </div>
      );
    }

    // External link embed
    if (embed.$type === 'app.bsky.embed.external#view' && embed.external) {
      const ext = embed.external;
      return (
        <a 
          href={ext.uri} 
          target="_blank" 
          rel="noopener noreferrer"
          className="post-external"
        >
          {ext.thumb && (
            <img src={ext.thumb} alt={ext.title} className="external-thumb" />
          )}
          <div className="external-text">
            <div className="external-title">{ext.title}</div>
            {ext.description && (
              <div className="external-desc">{ext.description}</div>
            )}
            <div className="external-uri">{new URL(ext.uri).hostname}</div>
          </div>
        </a>
      );
    }

    // Video embed
    if (embed.$type === 'app.bsky.embed.video#view' && embed.thumbnail) {
      return (
        <div className="post-video">
          <img src={embed.thumbnail} alt="Video thumbnail" />
          <div className="video-overlay">▶️ Video</div>
        </div>
      );
    }

    return null;
  };

  const levelClass = level === 'root' ? 'root' : level === 'parent' ? 'parent' : level === 'embedded' ? 'embedded' : 'target';
  const compactClass = compact ? 'compact' : '';

  return (
    <div className={`bluesky-post-card ${levelClass} ${compactClass}`}>
      <div className="post-header">
        {author?.avatar && (
          <img src={author.avatar} alt={author.displayName || author.handle} className="post-avatar" />
        )}
        <div className="post-author-info">
          <div className="post-author-name">{author?.displayName || author?.handle || 'Unknown'}</div>
          <div className="post-author-handle">@{author?.handle || 'unknown'}</div>
        </div>
        <div className="post-time">{formatTime(createdAt)}</div>
      </div>

      <div className="post-text">{displayText}</div>

      {renderEmbed()}

      {/* Only show stats in non-compact mode */}
      {!compact && (
        <div className="post-stats">
          {replyCount > 0 && <span>💬 {replyCount}</span>}
          {repostCount > 0 && <span>🔄 {repostCount}</span>}
          {likeCount > 0 && <span>❤️ {likeCount}</span>}
        </div>
      )}
    </div>
  );
};

export default BlueskyPostCard;

